import { useMemo, useRef, useState, type ChangeEvent } from "react";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { InputClearButton } from "./InputClearButton";
import { InputControl, InputSlot } from "./InputParts";
import { getInputRootClassName } from "./inputRecipe";
import type { InputProps } from "./Input.types";

/**
 * Sets the value through the prototype setter so React's value tracker
 * notices the change and the following `input` event reaches onChange
 * (controlled and uncontrolled alike, and every form library on top).
 */
const clearNativeValue = (input: HTMLInputElement) => {
    Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value",
    )?.set?.call(input, "");
    input.dispatchEvent(new Event("input", { bubbles: true }));
};

const hasText = (value: InputProps["value"] | InputProps["defaultValue"]) =>
    value !== undefined && value !== null && String(value) !== "";

/**
 * Design-system text input (spec docs/specs/2026-09-15-input-figma-rework.md).
 * Composes InputRoot chrome, an InputControl and optional slots. Everything
 * native lands on the <input>; className/style land on the root.
 * In uncontrolled mode the clear button reflects the DOM value on the next render.
 */
export const Input = ({
    variant = "primary",
    size,
    fontSize = 14,
    invalid = false,
    leftIcon,
    clearable = false,
    onClear,
    className,
    style,
    "data-testid": dataTestid,
    "data-forced-state": forcedState,
    ref,
    value,
    defaultValue,
    onChange,
    disabled,
    readOnly,
    ...props
}: InputProps) => {
    const resolvedSize = size ?? "md";
    const inputRef = useRef<HTMLInputElement | null>(null);
    const mergedRef = useMemo(
        () => mergeRefs([ref, inputRef]),
        [ref, inputRef],
    );
    const isControlled = value !== undefined;
    const [, setInputVersion] = useState(0);
    const uncontrolledValue = inputRef.current
        ? inputRef.current.value
        : defaultValue;
    const isFilled = isControlled ? hasText(value) : hasText(uncontrolledValue);
    const showsClearButton =
        clearable && isFilled && disabled !== true && readOnly !== true;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) {
            setInputVersion((version) => version + 1);
        }
        onChange?.(event);
    };

    const handleClear = () => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        clearNativeValue(input);
        input.focus();
        onClear?.();
    };

    return (
        <div
            className={getInputRootClassName({
                variant,
                size: resolvedSize,
                fontSize,
                className,
            })}
            style={style}
            data-variant={variant}
            data-size={resolvedSize}
            data-forced-state={forcedState}
        >
            {leftIcon !== undefined && (
                <InputSlot side="left">{leftIcon}</InputSlot>
            )}
            <InputControl
                ref={mergedRef}
                aria-invalid={invalid || undefined}
                value={value}
                defaultValue={defaultValue}
                onChange={handleChange}
                disabled={disabled}
                readOnly={readOnly}
                data-testid={dataTestid}
                {...props}
            />
            {showsClearButton && (
                <InputSlot side="right">
                    <InputClearButton onClick={handleClear} />
                </InputSlot>
            )}
        </div>
    );
};
